import { useCallback, useMemo } from "react";

import { Autocomplete, type AutocompleteRenderInputParams, type SxProps, TextField, type Theme } from "@mui/material";
import startCase from "lodash/startCase";

import { allocationTxt } from "../../../assets/texts/CloudAnalytics/allocation";
import { createFilterOptions } from "../../../Pages/CloudAnalytics/utilities";
import { type AttributionWRef } from "../../../types";
import { sortAttributions } from "./sortAttributions";

type Props = {
  allocation?: AttributionWRef | null;
  allocations: AttributionWRef[];
  disabledAttributions?: string[];
  disabled?: boolean;
  onChange: (allocation: AttributionWRef | null) => void;
  onClose?: () => void;
  sx?: SxProps<Theme>;
  placeholder?: string;
};

const getOptionLabel = (option: AttributionWRef) => option.data.name;

const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AttributionWRef) => (
  <li {...props} key={option.ref.id}>
    {option.data.name}
  </li>
);

const renderInput = (params: AutocompleteRenderInputParams) => (
  <TextField
    {...params}
    required
    variant="outlined"
    placeholder={allocationTxt.ADD_EXISTING_ALLOCATION}
    fullWidth
    sx={({ palette }) => ({ backgroundColor: palette.general.backgroundDefault })}
    size="small"
    slotProps={{
      inputLabel: {
        shrink: true,
      },
    }}
  />
);

export const AllocationSelect = ({
  allocation,
  allocations,
  disabled,
  disabledAttributions,
  onChange,
  onClose,
  sx,
}: Props) => {
  const sortedAllocations = useMemo(() => sortAttributions(allocations), [allocations]);

  const handleChange = useCallback(
    (_, value: AttributionWRef | null) => {
      onChange(value);
    },
    [onChange]
  );

  const getOptionDisabled = useCallback(
    (option: AttributionWRef) => disabledAttributions?.includes(option.ref.id) || false,
    [disabledAttributions]
  );

  const filterOptions = createFilterOptions<AttributionWRef>({
    trim: true,
    ignoreAccents: true,
    ignoreCase: true,
    matchFrom: "any",
  });

  return (
    <Autocomplete
      disabled={disabled}
      filterOptions={filterOptions}
      getOptionDisabled={getOptionDisabled}
      defaultValue={allocation || null}
      getOptionLabel={getOptionLabel}
      groupBy={(option) => startCase(option.data.type)}
      onChange={handleChange}
      onClose={onClose}
      options={sortedAllocations}
      renderInput={renderInput}
      renderOption={renderOption}
      sx={sx}
      value={allocation}
    />
  );
};
